import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import logger from "../utils/logger";

const WebSocketContext = createContext(null);
const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace("https://", "wss://").replace("http://", "ws://") + "/api/ws";

export function WebSocketProvider({ children }) {
  const wsRef = useRef(null);
  const [lastEvent, setLastEvent] = useState(null);
  const listenersRef = useRef(new Map());
  const reconnectTimerRef = useRef(null);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;
    try {
      const ws = new WebSocket(WS_URL);
      ws.onopen = () => {
        const heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) ws.send("ping");
        }, 30000);
        ws._heartbeat = heartbeat;
      };
      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.event === "pong") return;
          setLastEvent(msg);
          const callbacks = listenersRef.current.get(msg.event) || [];
          callbacks.forEach((cb) => cb(msg.data));
        } catch (err) {
          logger.error("WS message parse error:", err);
        }
      };
      ws.onclose = () => {
        clearInterval(ws._heartbeat);
        reconnectTimerRef.current = setTimeout(connect, 3000);
      };
      ws.onerror = () => ws.close();
      wsRef.current = ws;
    } catch (err) {
      logger.error("WS connect error:", err);
      reconnectTimerRef.current = setTimeout(connect, 5000);
    }
    // WS_URL is a module-level constant — no dependency needed
  }, []);

  useEffect(() => {
    connect();
    return () => {
      clearTimeout(reconnectTimerRef.current);
      if (wsRef.current) {
        clearInterval(wsRef.current._heartbeat);
        wsRef.current.close();
      }
    };
  }, [connect]);

  const subscribe = useCallback((event, callback) => {
    if (!listenersRef.current.has(event)) {
      listenersRef.current.set(event, []);
    }
    listenersRef.current.get(event).push(callback);
    return () => {
      const cbs = listenersRef.current.get(event) || [];
      listenersRef.current.set(event, cbs.filter((cb) => cb !== callback));
    };
  }, []);

  const value = useMemo(
    () => ({ lastEvent, subscribe }),
    [lastEvent, subscribe]
  );

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}

export function useWebSocket() {
  return useContext(WebSocketContext);
}
